<script setup lang="ts">
import {ref, onMounted } from "vue";
import { useRouter } from "vue-router";

import CommonError from '@/components/CommonError.vue';
import CommonLoader from '@/components/CommonLoader.vue';
import StatusTag from '@/components/StatusTag.vue';
import { getTagColor } from "@/utils";
import { getCustomerList } from '@/api'
import { Customer } from '@/types/Customer';
import { getTaxInfoTypeValue } from "@/utils/getValueForKey";
import { ElMessageBox } from 'element-plus';
import type { TableColumnCtx, TableInstance } from 'element-plus';



const loading = ref<boolean>(false);
const isError = ref<boolean>(false);

const data = ref<Customer[]>([])
const totalCount = ref<number>(0);
const currentPage = ref<number>(1);
const pageSize = ref<number>(100);
const searchKeyword = ref('');

const sortValue = ref<string | undefined>('companyName');
const selectedFilters = ref<{ [key: string]: string[] } | undefined>({});

const fetchData = async () => {
  loading.value = true;
  isError.value = false;
  const params: Record<string, any> = {
    pageNum: currentPage.value,
    pageSize: pageSize.value,
  }

  if (searchKeyword.value.length >= 2) {
    params.userId = searchKeyword.value;
  }

  if (sortValue.value) {
    params.sort = sortValue.value;
  }

  if (selectedFilters.value) {
    const searchFilters: Record<string, any> = {};
    Object.entries(selectedFilters.value).forEach(([key, value]) => {
      if (value.length > 0) {
        searchFilters[key] = value;
      }
    });

    if (Object.keys(searchFilters).length > 0) {
      params.search = searchFilters;
    }
  }

  try {
    const response = await getCustomerList(params);
    if (response.success) {
      totalCount.value = response.result.count;
      data.value = response.result.list;
    }
  } catch (error) {
    loading.value = false;
    isError.value = true;
    console.error('Error Message:', error);
  } finally {
    loading.value = false;
  }
}

fetchData()

const clearSearch = () => {
  searchKeyword.value = ';'
}
const handleSearch = () => {
  if(searchKeyword.value.length < 2) {
    ElMessageBox.alert('검색어는 2자 이상 입력해주세요.', {
      confirmButtonText: '확인',
    });
    return;
  }
  fetchData();
}

const router = useRouter();

const handleSizeChange = (size: number) => {
  pageSize.value = size;
  fetchData();
}
const handlePageChange = (page: number) => {
  currentPage.value = page;
  fetchData();
};

const handleSortChange = ({ prop, order }: { prop: string; order: string }) => {
  if (order === "ascending") {
    sortValue.value = prop;
  } else if (order === "descending") {
    sortValue.value = `-${prop}`
  } else {
    sortValue.value = undefined;
  }
  fetchData();
};

const handleFilterChange = (filters: Record<string, string[]>) => {
  selectedFilters.value = filters || [];
  fetchData();
};

const tableRef = ref<TableInstance>();

const handleReset = () => {
  searchKeyword.value = '';
  currentPage.value = 1;
  pageSize.value = 100;
  tableRef.value?.clearFilter();
  tableRef.value?.clearSort();
  sortValue.value = 'companyName';
  selectedFilters.value = undefined;
  fetchData(); 
}

const toCustomerView = (id: string) => {
  console.log(id)
  router.push(`/customer/view/${id}`)
}

</script>
<template>
  <div class="page-manage">
    <div class="search-area">
      <el-input
        v-model="searchKeyword"
        placeholder="뷰저블 계정 또는 회사명을 입력하세요."
        clearable
        @clear="clearSearch"
        size="large"
        style="width: 300px"
        class="input-search"
        @keyup.enter="handleSearch"
      >
        <template #append>
          <el-button
            aria-label="검색"
            @click="handleSearch"
          >
            <font-awesome-icon icon="fa-solid fa-magnifying-glass" />
          </el-button>
        </template>
      </el-input>
      <el-button
        type="info"
        size="large"
        class="btn-reset"
        @click="handleReset"
      >
        <font-awesome-icon icon="fa-solid fa-rotate-left" />
      </el-button>
    </div>
    <template v-if="!loading && !isError">
      <el-table
        :data="data"
        :default-sort="{ prop: 'id', order: 'descending' }"
        style="width: 100%"
        ref="tableRef"
        @sort-change="handleSortChange"
        @filter-change="handleFilterChange"
      >
        <template #empty>
          <span v-if="searchKeyword">
            '{{ searchKeyword }}' 고객을 찾을 수 없습니다
          </span>
          <span v-else>고객 데이터가 없습니다.</span>
        </template>
        <el-table-column
          label="자세히"
          width="60"
          align="center"
          fixed
        >
          <template #default="scope">
            <el-button
              size="small"
              aria-label="자세히"
              @click="toCustomerView(scope.row.customerId)"
            >
              <font-awesome-icon icon="fa-regular fa-file-lines" />
            </el-button>
          </template>
        </el-table-column>
        <el-table-column
          prop="companyName"
          label="회사명"
          fixed
          width="220"
          sortable="custom"
        >
          <template #default="scope">
            {{ scope.row.companyName }}<br/>
          </template>
        </el-table-column>
        <el-table-column
          prop="userId"
          label="뷰저블 계정"
          fixed
          width="240"
          sortable="custom"
        >
          <template #default="scope">
            {{ scope.row.userId }}
          </template>
        </el-table-column>
        <el-table-column
          prop="managerName"
          label="담당자"
          width="220"
          align="center"
        >
          <template #default="scope">
            {{ scope.row.managerName.join(', ') }}
          </template>
        </el-table-column>
        <el-table-column
          prop="taxInfoType"
          :label="`세금 계산서 발행 방식`"
          width="200"
          align="center"
          column-key="taxInfoType"
        >
          <template #default="scope">
            <status-tag 
              v-if="scope.row.taxInfoType"
              size="small" 
              :color="getTagColor(scope.row.taxInfoType)"
              :text="getTaxInfoTypeValue(scope.row.taxInfoType)"
            />
            <template v-else>
              -
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="memo"
          label="메모"
        >
        <template #default="scope">
          <span v-if="scope.row.memo">{{ scope.row.memo }}</span>
          <div 
            v-else 
            :style="{textAlign: 'center'}"
          >-</div>
        </template>
        </el-table-column>
    </el-table>
    <div class="pagination-area">
      <div class="count">
        총 <strong>{{ totalCount }}</strong>개의 고객사
      </div>
      <el-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="[30, 50, 100]"
        :total="totalCount"
        layout="sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>
    </template>
    <template v-else-if="loading">
      <CommonLoader />
    </template>
    <template v-else>
      <CommonError message="서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.<br/> 문제가 지속되면 관리자에게 문의하세요." />
    </template>
  </div>
</template>